import { styled } from 'linaria/lib/react';
import noiseTexture from 'assets/images/noise.png';
import { BREAKPOINTS, COLORS, FONTS } from 'styles/constants';

export const StyledSparksListContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: ${BREAKPOINTS.s}px;
  min-height: 200px;
  position: relative;
  width: calc(100% - 24px);
  z-index: 1;
`;

export const StyledSparkCardWrapper = styled.div`
  padding: 0 12px;
  margin-bottom: 56px;

  @media screen and (min-width: ${BREAKPOINTS.s}px) {
    padding: 0;
  }
`;

export const StyledOverlay = styled.div<{ isDark: boolean; startColor: string; height: number; isMyProfile?: boolean }>`
  position: absolute;
  top: 0;
  left: calc(50% - 50vw);
  width: 100vw;
  height: ${({ height }) => `${height - 40}px`};
  pointer-events: none;
  z-index: 0;
  background: url(${noiseTexture}),
    ${({ startColor, isDark, isMyProfile }) => {
      const topStop = isMyProfile === false ? '20%' : '5%';
      return `linear-gradient(180deg, ${startColor} ${topStop}, ${startColor} 65%, ${
        isDark ? COLORS.black[100] : COLORS.white[100]
      } 100%)`;
    }};
  -webkit-mask-image: ${({ isMyProfile }) =>
    `linear-gradient(180deg, rgba(0, 0, 0, 1) ${isMyProfile === false ? '20%' : '5%'}, rgba(0, 0, 0, 0) 100%)`};
  mask-image: ${({ isMyProfile }) =>
    `linear-gradient(180deg, rgba(0, 0, 0, 1) ${isMyProfile === false ? '20%' : '5%'}, rgba(0, 0, 0, 0) 100%)`};
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  pointer-events: none;
`;

export const StyledEmptyState = styled.div<{
  fontColor?: string;
}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  padding: 0 24px 48px 24px;
  text-align: center;
`;

export const StyledIllustrationImage = styled.img`
  border-radius: 16px;
  height: auto;
  margin: 0 auto 24px auto;
  max-width: 350px;
  width: 100%;
  z-index: 1;
`;

export const StyledEmptyText = styled.p<{
  fontColor?: string;
  isDark?: boolean;
}>`
  font: 600 14px/140% ${FONTS.secondary};
  color: ${({ fontColor, isDark }) => fontColor ?? (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  text-align: center;
`;

export const StyledBrowseSparksButton = styled.button<{ isDark?: boolean }>`
  align-items: center;
  backdrop-filter: blur(2px);
  background: transparent;
  border: 1px solid ${({ isDark }) => (isDark ? COLORS.white[30] : COLORS.brown.dark[30])};
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  gap: 4px;
  justify-content: center;
  padding: 10px 20px 10px 12px;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ isDark }) => (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')};
  }

  &:active {
    background: ${({ isDark }) => (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)')};
  }
`;

export const StyledSparkIconVideo = styled.video`
  height: 24px;
  object-fit: contain;
  width: 24px;
`;

export const StyledButtonText = styled.span<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  font: 600 14px/140% ${FONTS.secondary};
`;

export const StyledSparkItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${COLORS.white[10]};
`;

// Spark Card Styles
export const StyledSparkCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  width: 100%;
`;

export const StyledSparkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 10px;
  width: 100%;
`;

export const StyledSparkCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 0 12px;
`;

export const StyledSparkCardAvatar = styled.div<{ src?: string }>`
  background-color: ${COLORS.brown.light[100]};
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 60px;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
`;

export const StyledSparkCardTitle = styled.span`
  color: ${COLORS.white[100]};
  flex: 1;
  font: 600 14px/1.4 ${FONTS.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledSparkCardCover = styled.div<{ bgImage?: string }>`
  aspect-ratio: 342 / 430;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

export const StyledSparkCardCoverVideo = styled.video`
  height: 100%;
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

export const StyledSparkCardFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  height: 20px;
  padding: 0 12px 0 8px;
`;

export const StyledSparkCardSource = styled.div`
  flex: 1;
`;

export const StyledSparkCardButtons = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
`;

export const StyledSparkCardButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 22px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: ${COLORS.white[10]};
  }

  svg {
    fill: ${COLORS.white[100]};
    height: 24px;
    width: 24px;

    path {
      fill: ${COLORS.white[100]};
    }
  }
`;
